import { useState } from "react";
import { Link } from "react-router-dom";
import { common_utils } from "../../../../../../common/common_utils";
import { isSeshProProduct, isBundleProduct, PaymentInterval, PaymentIntervalToActiveName, getRawProductWithoutPrices } from "../../../../../../common/premium_tiers";
import { DashboardUtils } from "../../../dashboard_utils";
import { trpc } from "../../../utils/trpc";
import { InfoTooltip } from "../tooltips/info_tooltip";
import { DiscordEntitlement } from "./entitlements/discord_entitlement";
import { GenericEntitlement } from "./entitlements/generic_entitlement";
import { PatreonEntitlement } from "./entitlements/patreon_entitlement";
import { StripeEntitlement } from "./entitlements/stripe_entitlement";
import { PremiumServerListingsModal, SeshProServerListingsModal } from "./premium_server_listings_modal";
import { UseQueryResult } from "@tanstack/react-query";

interface UpgradeableLicense {
	id: string;
	product_id: string;
	is_bundle: boolean;
	available_pro_slots: number;
	current_pro_addon_count: number;
	payment_interval?: number;
}

export function PremiumActivePurchaseListings({
	premium_details_query,
	upgradeable_licenses,
	onUpgrade,
}: {
	premium_details_query: UseQueryResult<any, unknown>;
	upgradeable_licenses?: UpgradeableLicense[];
	onUpgrade?: (license: UpgradeableLicense) => void;
}) {
	const [premium_modal_opened, setPremiumModalOpened] = useState(false);
	const [sesh_pro_modal_opened, setSeshProModalOpened] = useState(false);
	const [invoices_modal_opened, setInvoicesModalOpened] = useState(false);

	if (!premium_details_query.isSuccess) {
		return <></>;
	}

	// Separate entitlements into Premium and sesh Pro
	// Hide bundles that have all slots converted to Pro (pro_addon_count >= 3)
	const premium_entitlements = premium_details_query.data.stripe_entitlements
		.filter((val) => !isSeshProProduct(val.product_id))
		.filter((val) => !isBundleProduct(val.product_id) || (val.pro_addon_count ?? 0) < 3);
	const sesh_pro_entitlements = premium_details_query.data.stripe_entitlements
		.filter((val) => isSeshProProduct(val.product_id));
	// Get Pro addons from bundles (bundles with pro_addon_count > 0)
	const bundle_pro_addons = premium_details_query.data.stripe_entitlements
		.filter((val) => isBundleProduct(val.product_id) && (val.pro_addon_count ?? 0) > 0);

	// Calculate if Premium servers are being covered by unassigned Pro slots
	const premiumActivated = premium_details_query.data.transferrable_activated_servers_count;
	const premiumMax = premium_details_query.data.max_activated_servers;
	const proMax = premium_details_query.data.max_sesh_pro_activations;
	const proActivated = premium_details_query.data.sesh_pro_activated_servers_count;
	const unassignedPro = Math.max(0, proMax - proActivated);
	const premiumOverLimit = premiumActivated > premiumMax;
	const serversCoveredByPro = premiumOverLimit ? Math.min(unassignedPro, premiumActivated - premiumMax) : 0;

	return (
		<>
			{premium_details_query.data.discord_entitlements.length > 0 && (
				<div className="flex flex-col gap-2">
					<div className="flex items-center gap-2">
						<div className="flex gap-2 text-lg font-bold">
							<div>Discord Purchases</div>
							<div className="flex items-center">
								<InfoTooltip
									info={
										"List of sesh premium purchases bought through Discord. Each individual subscription is tied to the server it is purchased for, whereas one-time purchases can be freely transferred between servers. If you wish to modify these subscriptions, you must do so through Discord."
									}
								/>
							</div>
						</div>
					</div>
					<div className="flex w-full flex-col gap-2">
						{premium_details_query.data.discord_entitlements
							.length > 0 &&
							premium_details_query.data.discord_entitlements.map(
								(val) => (
									<DiscordEntitlement {...val} key={val.id} />
								)
							)}
					</div>
				</div>
			)}
			{premium_details_query.data.max_activated_servers > 0 && (
				<div className="flex flex-col gap-2">
					<div className="flex flex-wrap items-center gap-2">
						<div className="flex gap-2 text-lg font-bold">
							<div>Premium</div>
							<div className="flex items-center">
								<InfoTooltip
									info={
										"List of sesh premium subscriptions bought through Stripe or Patreon. These subscriptions can be transferred to other servers freely. If you subscribe to more than one tier, your premium server activation count will include all of your purchases and be cumulative."
									}
								/>
							</div>
						</div>
						<div className="ml-auto flex items-center gap-2">
							<span>
								Activations:{" "}
								{premiumActivated}/{premiumMax}
							</span>
							{serversCoveredByPro > 0 && (
								<InfoTooltip
									info={
										<>
											{serversCoveredByPro} server{serversCoveredByPro > 1 ? "s are" : " is"} temporarily
											using unassigned sesh Pro slots. Assign your Pro licenses to servers
											in the sesh Pro section below.
										</>
									}
								/>
							)}
							<span
								className="manual-link"
								onClick={() => setPremiumModalOpened(true)}
							>
								Manage
							</span>
						</div>
					</div>
					<div className="flex w-full flex-col gap-2">
						{premium_entitlements.map((val) => {
							const upgradeableLicense = upgradeable_licenses?.find(l => l.id === val.id);
							return (
								<StripeEntitlement
									{...val}
									key={val.id}
									isUpgradeable={!!upgradeableLicense}
									onUpgrade={upgradeableLicense && onUpgrade ? () => onUpgrade(upgradeableLicense) : undefined}
								/>
							);
						})}
						{premium_details_query.data.patreon_activations > 0 &&
							premium_details_query.data
								.is_patreon_purely_from_override && (
								<PatreonEntitlement
									{...premium_details_query.data}
								/>
							)}
					</div>
				</div>
			)}
			{premium_details_query.data.max_sesh_pro_activations > 0 && (
				<div className="flex flex-col gap-2">
					<div className="flex flex-wrap items-center gap-2">
						<div className="flex gap-2 text-lg font-bold">
							<div>sesh Pro</div>
							<div className="flex items-center">
								<InfoTooltip
									info={
										"sesh Pro subscriptions include all Premium features plus API access and custom branding. These activations can be transferred between servers."
									}
								/>
							</div>
						</div>
						<div className="ml-auto">
							Activations:{" "}
							{
								premium_details_query.data
									.sesh_pro_activated_servers_count
							}
							/{premium_details_query.data.max_sesh_pro_activations}{" "}
							<span
								className="manual-link"
								onClick={() => setSeshProModalOpened(true)}
							>
								Manage
							</span>
						</div>
					</div>
					<div className="flex w-full flex-col gap-2">
						{/* Standalone sesh Pro entitlements */}
						{sesh_pro_entitlements.map((val) => {
							const product = getRawProductWithoutPrices(val.product_id);
							const isOneTime = val.payment_interval === PaymentInterval.OneTime;
							return (
								<GenericEntitlement
									key={val.id}
									title={product?.title || "sesh Pro"}
									type="Stripe"
									payment_interval={PaymentIntervalToActiveName[val.payment_interval as PaymentInterval].toLowerCase()}
									modify_link={
										!isOneTime ? (
											<Link
												className="secondary-link"
												to={common_utils.getStripeRedirectURL({
													type: "manager",
												})}
											>
												Modify
											</Link>
										) : undefined
									}
									details={
										<>
											<div>
												Activations Granted:{" "}
												<span className="font-bold">{val.activations}</span>
											</div>
											<div>
												Status:{" "}
												<span className="font-bold">{val.active_status ?? "active"}</span>
											</div>
										</>
									}
								/>
							);
						})}
						{/* Pro addons from bundles */}
						{bundle_pro_addons.map((val) => {
							const proCount = val.pro_addon_count ?? 0;
							const isOneTime = val.payment_interval === PaymentInterval.OneTime;
							return (
								<GenericEntitlement
									key={`${val.id}-pro-addon`}
									title="sesh Pro Addon"
									type="Stripe"
									payment_interval={PaymentIntervalToActiveName[val.payment_interval as PaymentInterval].toLowerCase()}
									modify_link={
										!isOneTime ? (
											<Link
												className="secondary-link"
												to={common_utils.getStripeRedirectURL({
													type: "manager",
												})}
											>
												Modify
											</Link>
										) : undefined
									}
									details={
										<>
											<div>
												Activations Granted:{" "}
												<span className="font-bold">{proCount}</span>
											</div>
											<div>
												Status:{" "}
												<span className="font-bold">{val.active_status ?? "active"}</span>
											</div>
											<div className="text-slate-400">
												(from Premium Bundle)
											</div>
										</>
									}
								/>
							);
						})}
					</div>
				</div>
			)}
			{/* View All Invoices button - show when user has any Stripe purchases */}
			{premium_details_query.data.stripe_entitlements.length > 0 && (
				<div className="flex justify-end">
					<button
						className="secondary-link flex items-center gap-1"
						onClick={() => setInvoicesModalOpened(true)}
					>
						<i className="fas fa-file-invoice fa-sm" />
						View All Invoices
					</button>
				</div>
			)}
			{premium_modal_opened && (
				<PremiumServerListingsModal
					premium_details={premium_details_query.data}
					onClose={() => setPremiumModalOpened(false)}
				/>
			)}
			{sesh_pro_modal_opened && (
				<SeshProServerListingsModal
					premium_details={premium_details_query.data}
					onClose={() => setSeshProModalOpened(false)}
				/>
			)}
			{invoices_modal_opened && (
				<InvoicesModal onClose={() => setInvoicesModalOpened(false)} />
			)}
		</>
	);
}

function InvoicesModal({ onClose }: { onClose: () => void }) {
	const invoicesQuery = trpc.premium.getAllInvoices.useQuery();

	const formatDate = (timestamp: number) => {
		return new Date(timestamp * 1000).toLocaleDateString("en-US", {
			year: "numeric",
			month: "short",
			day: "numeric",
		});
	};

	const formatAmount = (amount: number, currency: string) => {
		return new Intl.NumberFormat("en-US", {
			style: "currency",
			currency: currency.toUpperCase(),
		}).format(amount / 100);
	};

	const formatInterval = (interval: "month" | "year" | "one_time") => {
		switch (interval) {
			case "month":
				return "Monthly";
			case "year":
				return "Yearly";
			case "one_time":
				return "One-time";
		}
	};

	return (
		<DashboardUtils.SeshModal
			is_active={true}
			onClose={onClose}
			title="Invoices & Receipts"
			subtitle="View your payment history"
			content={
				<div className="mt-4 flex h-fit max-h-[90%] min-h-0 overflow-auto">
					{invoicesQuery.isLoading && (
						<div className="flex w-full items-center justify-center py-8">
							<i className="fas fa-spinner fa-spin fa-lg" />
						</div>
					)}
					{invoicesQuery.isError && (
						<div className="flex w-full items-center justify-center py-8 text-red-400">
							Failed to load invoices
						</div>
					)}
					{invoicesQuery.isSuccess && invoicesQuery.data.invoices.length === 0 && (
						<div className="flex w-full items-center justify-center py-8 text-slate-400">
							No invoices found
						</div>
					)}
					{invoicesQuery.isSuccess && invoicesQuery.data.invoices.length > 0 && (
						<ul className="flex min-h-0 w-full flex-col gap-2 overflow-auto">
							{invoicesQuery.data.invoices.map((invoice) => (
								<li
									key={invoice.id}
									className="flex items-center gap-4 rounded-sm bg-base-light p-3 shadow-sm"
								>
									<div className="flex flex-1 flex-col gap-1">
										<div className="font-semibold">
											{invoice.description || "Payment"}
										</div>
										<div className="flex items-center gap-2 text-sm text-slate-400">
											<span>{formatDate(invoice.date)}</span>
											<span className="rounded bg-base-dark px-1.5 py-0.5 text-xs">
												{formatInterval(invoice.interval)}
											</span>
										</div>
									</div>
									<div className="flex items-center gap-4">
										<div className="text-right">
											<div className="font-bold">
												{formatAmount(invoice.amount, invoice.currency)}
											</div>
											<div className="text-xs text-slate-400 capitalize">
												{invoice.status}
											</div>
										</div>
										{invoice.url && (
											<a
												href={invoice.url}
												target="_blank"
												rel="noopener noreferrer"
												className="secondary-link flex items-center gap-1"
											>
												View <i className="fas fa-external-link-alt fa-xs" />
											</a>
										)}
									</div>
								</li>
							))}
						</ul>
					)}
				</div>
			}
		/>
	);
}
